import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Table } from "react-bootstrap";
import { Skeleton, TableCell, Tooltip, InputLabel } from "@mui/material";
import Select from "react-select";
import { DateSelectorWithoutFormik } from "../../common/textfield/DateSelector";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { searchEncounters } from "../api/AppointmentRequests";
import { failed, success } from "../../common/Toastify";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import ViewIcon from "../../../assets/images/view.png";
import "./DashboardEncounterList.css";
import { Button } from "../../common/Button";
import { getEncounterRecord } from "../api/AppointmentRequests";
import { set } from "date-fns";
import { ViewEncounterModal } from "./ViewEncounterModal";
import { ViewDateTime } from "../../../utils/DateSupport";
import { encryptData } from "../../../components/EncryptDecrypt";
import RefreshIcon from '@mui/icons-material/Refresh';


const DashboardEncounterList = (props) => {
    const navigate = useNavigate();
    const user = useSelector((state) => state?.auth?.user);
    const userOrganizationId = useSelector(
        (state) => state?.auth?.user?.["organizations"]?.[0]?.id
    );
    const practitionerId = user?.["custom:practitioner_id"];

    const [encounterData, setEncounterData] = useState([]);
    const [encounterCount, setEncounterCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [encounterStartDate, setEncounterStartDate] = useState(
        moment().subtract(7, "days").format("YYYY-MM-DD")
    );
    const [encounterEndDate, setEncounterEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [page, setPage] = useState(0);
    const rowsPerPage = 5;
    const [isPagination, setIsPagination] = useState(false)
    const [encounterModal, setEncounterModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const organizations = props.organizations.map(org => ({label: org.name, value: org.value}));
    const [orgId, setOrgId] = useState(userOrganizationId);
    const handleOrgChange = (event) => {
        setOrgId(event.value);
    }

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
        setIsPagination(false);
    }, [encounterStartDate, encounterEndDate, orgId]);

    // Fetch data when page changes or when filters change
    useEffect(() => {
        fetchEncounters();
    }, [encounterStartDate, encounterEndDate, orgId, page]);


    const fetchEncounters = () => {
        setIsLoading(true);
        let encounterStartDateEpoch = moment(encounterStartDate).startOf('day').valueOf();
        let encounterEndDateEpoch = moment(encounterEndDate).endOf('day').valueOf();
        searchEncounters({
            orgId: orgId,
            start: encounterStartDateEpoch,
            end: encounterEndDateEpoch,
            practitionerId: practitionerId,
            page: isPagination ? page + 1 : 1,
            pageSize: rowsPerPage,
            type: 'open',
            getAll: true, 
        })
            .then((res) => {
                const sortedData = sortByStartDateDesc(res?.data || []);
                setEncounterData(sortedData);
                setEncounterCount(res?.count?.Count || 0);
            })
            .catch((err) => {
                failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
            })
            .finally(() => setIsLoading(false));
    };


    const sortByStartDateDesc = (data) => {
        return data.sort((a, b) => {
            const dateA = new Date(a.period?.start);
            const dateB = new Date(b.period?.start);
            return dateB - dateA;
        });
    };

    const handleViewEncounterClick = (encounter) => {
        const encounterId = encounter.id;
        getEncounterRecord({ encounterId })
            .then((res) => {
                setSelectedRecord(res?.data);
                setEncounterModal(true);
            })
            .catch((err) => {
                failed(err?.response?.data?.message || err.message);
            });
    };

    const handlePagination = (pageNo) => {
        setPage(pageNo);
        setIsPagination(true);
    };

    const fetchEncountersRef = useRef();
    
    useEffect(() => {
        fetchEncountersRef.current = fetchEncounters;
    });

    const refreshEncounters = useCallback(() => {
        if (fetchEncountersRef.current) {
            fetchEncountersRef.current();
        }
    }, []);

    useEffect(() => {
        if (props.onRefreshReady) {
            props.onRefreshReady(refreshEncounters);
        }
    }, [refreshEncounters, props.onRefreshReady]);

    return (
        <div className="dashboard-encounter-list">
            <div className="mnth-date-wrap">
                <DateSelectorWithoutFormik
                    label="Start Date"
                    minDate={""}
                    maxDate={moment(encounterEndDate)}
                    value={moment(encounterStartDate)}
                    handleChange={(val) => setEncounterStartDate(val.format("YYYY-MM-DD"))}
                    type="readonly"
                />
                <DateSelectorWithoutFormik
                    label="End Date"
                    minDate={moment(encounterStartDate).add(1, "days")}
                    maxDate={moment()}
                    value={moment(encounterEndDate)}
                    handleChange={(val) => setEncounterEndDate(val.format("YYYY-MM-DD"))}
                    type="readonly"
                />
                {organizations.length > 1 &&
                
                <div className="field-wrap" style={{width:200}}>
                    <InputLabel
                        id="mutiple-select-label"
                        style={{ fontSize: "0.75rem", color: "black", marginBottom: "8px" }}
                    >
                        {"Organization"}
                    </InputLabel>
                    <Select
                        keyField={"organization"}
                        label={"Select Organization"}
                        selectLabel="Select"
                        options={organizations}
                        isLoading={organizations?.length > 1 ? false : true}
                        readOnly={organizations?.length > 1 ? false : true}
                        hideRequired={true}
                        onChange={handleOrgChange}
                        className="search-select"
                        classNamePrefix="select"
                    />
                </div>}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-start", paddingTop: "24px" }}>
                    <Tooltip title="Refresh">
                        <Button
                            onClick={refreshEncounters}
                            disabled={isLoading}
                            isLoading={isLoading}
                            startIcon={<RefreshIcon />}
                        >
                            Refresh
                        </Button>
                    </Tooltip>
                </div>
            </div>

            <div className="table-wrap">
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Start Date</th>
                            <th>Encounter ID</th>
                            <th>Patients Name</th>
                            <th>Organization</th>
                            <th>Status</th>
                            <th>Report</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading
                            ? Array.from({ length: rowsPerPage }).map((_, idx) => (
                                <tr key={idx}>
                                    {Array.from({ length: 7 }).map((_, cellIdx) => (
                                        <td key={cellIdx}>
                                            <Skeleton animation="wave" />
                                        </td>
                                    ))}
                                </tr>
                            ))
                            : encounterData?.length > 0
                                ? encounterData.map((encounter) => {
                                    const { id, subject, period, status, orgId } = encounter;
                                    const patientName = subject?.display || "-";

                                    return (
                                        <tr key={id}>
                                            <td>{period?.start ? ViewDateTime(period?.start) : '-'}</td>
                                            <td>{id || "-"}</td>
                                            <td>
                                                {patientName !== "-" ? (
                                                    <span
                                                        onClick={() => window.open(`/app/patients-details?patientId=${subject?.reference?.split("/")[1]}`, '_blank')}
                                                        style={{ cursor: "pointer", textDecoration: "underline", textTransform: "capitalize" }}
                                                    >
                                                        {patientName}
                                                    </span>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td>
                                                {organizations.find(org => org.value === orgId)?.label || "-"}
                                            </td>
                                            <td>{status || "-"}</td>
                                            <td>
                                                {status !== "cancelled" && !(status === "in-progress-intake" && patientName === "-") && (
                                                    <div className="action-wrap" style={{ justifyContent: "start" }}>
                                                        <div
                                                            onClick={() => {
                                                                navigate("/app/encounter-details", {
                                                                    state: { encounterId: id, notPatientDashboard: true },
                                                                })
                                                            }}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <Tooltip title={"View"}>
                                                                <Link to="" variant="primary" className="view-btn">
                                                                    <img src={ViewIcon} alt="View" />
                                                                </Link>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                ) || "-"}
                                            </td>
                                            <td>
                                                <div
                                                    className="action-wrap"
                                                    style={{
                                                        justifyContent: "start",
                                                    }}
                                                >
                                                    <Button
                                                        title="View"
                                                        onClick={() => handleViewEncounterClick(encounter)}
                                                    >
                                                        View
                                                    </Button>
                                                </div>


                                            </td>
                                        </tr>
                                    );
                                })
                                : (
                                    <tr>
                                        <TableCell colSpan={5} style={{ textAlign: "center" }}>
                                            No data available
                                        </TableCell>
                                    </tr>
                                )}
                    </tbody>
                </Table>

                {encounterData?.length > 0 && Array.isArray(encounterData) ? (
                    <CustomPagination
                        tableData={encounterData}
                        totalRows={encounterCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        handlePage={handlePagination}
                    />
                ) : null}

                {encounterModal && selectedRecord && (
                    <ViewEncounterModal
                        data={selectedRecord}
                        onShow={encounterModal}
                        onHide={() => setEncounterModal(false)}
                        refreshEncounters={refreshEncounters} // Pass the refresh function
                    />
                )}

            </div>
        </div>
    );
};

export default DashboardEncounterList;
